import React from 'react';
import { Controller, Control, FieldError } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import { PriorityDto } from '../../types';

interface PriorityDropdownProps {
  name: string;
  control: Control<any>;
  error?: FieldError;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  showCode?: boolean;
}

const PriorityDropdown: React.FC<PriorityDropdownProps> = ({
  name,
  control,
  error,
  disabled = false,
  required = false,
  placeholder = "Select Priority",
  className = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color",
  showCode = true
}) => {
  // Fetch priorities from API
  const { data: prioritiesResponse, isLoading: prioritiesLoading } = useQuery({
    queryKey: ['priorities'],
    queryFn: () => apiService.getPriorities(),
    retry: 1
  });

  const priorities = prioritiesResponse?.data?.sort((a, b) => a.sortOrder - b.sortOrder) || [];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <>
          <select
            {...field}
            className={className}
            disabled={disabled || prioritiesLoading}
          >
            <option value={0}>{placeholder}</option>
            {priorities.map(priority => (
              <option 
                key={priority.id} 
                value={priority.id}
                style={{ 
                  backgroundColor: priority.colorCode + '20', // 20% opacity
                  color: priority.colorCode,
                  fontWeight: '500'
                }}
              >
                {showCode ? `${priority.code} (${priority.name})` : priority.name}
              </option>
            ))}
          </select>
          {error && (
            <p className="mt-1 text-sm text-red-600">{error.message}</p>
          )}
        </>
      )}
    />
  );
};

export default PriorityDropdown;
